import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatDate, formatDateTime } from "../../lib/format";
import type { ListResponse, Payout } from "../../lib/types";
import {
  Button,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  PageHeader,
  TextAreaField,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

type Action = { kind: "processing" | "confirm" | "fail"; payout: Payout } | null;

export function AdminPayouts() {
  const queryClient = useQueryClient();
  const [action, setAction] = useState<Action>(null);
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");

  const payouts = useQuery({
    queryKey: ["admin-payouts"],
    queryFn: () => api.get<ListResponse<Payout>>("/api/admin/payouts?limit=100"),
    refetchInterval: 30_000,
  });

  const act = useMutation({
    mutationFn: () => {
      if (!action) throw new Error("no action");
      const { kind, payout } = action;
      if (kind === "processing") return api.post(`/api/admin/payouts/${payout.id}/processing`);
      if (kind === "confirm")
        return api.post(`/api/admin/payouts/${payout.id}/confirm`, { bank_reference: reference });
      return api.post(`/api/admin/payouts/${payout.id}/fail`, { reason });
    },
    onSuccess: () => {
      setAction(null);
      setReference("");
      setReason("");
      queryClient.invalidateQueries({ queryKey: ["admin-payouts"] });
    },
  });

  const rows = payouts.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Payout runs"
        description="Settlements waiting to be instructed at the bank, and those already in flight."
      />

      <Sheet className="settle settle-1 overflow-hidden">
        <SheetHeader title="Open payouts" description="Everything not yet paid or failed" />
        {payouts.isLoading ? (
          <Skeleton />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing to settle"
            description="Payouts appear here once they are created for a firm with cleared funds."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Payout</Th>
                <Th>Status</Th>
                <Th>Destination</Th>
                <Th align="right">Payments</Th>
                <Th align="right">Amount</Th>
                <Th align="right">Created</Th>
                <Th align="right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((payout) => (
                <Tr key={payout.id}>
                  <Td mono>
                    <span className="text-ink">{payout.id.slice(0, 14)}…</span>
                    <span className="mt-0.5 block text-[11px] text-ink-faint">
                      {formatDate(payout.period_start)} — {formatDate(payout.period_end)}
                    </span>
                  </Td>
                  <Td>
                    <StatusPill status={payout.status} />
                  </Td>
                  <Td>
                    <span className="text-[12.5px] text-ink">
                      {payout.destination.account_name}
                    </span>
                    <span className="mt-0.5 block font-mono text-[11px] text-ink-faint">
                      {payout.destination.bank_name} {payout.destination.account_number_masked}
                    </span>
                  </Td>
                  <Td align="right" className="tabular">
                    {payout.payment_count}
                  </Td>
                  <Td align="right" mono className="font-medium">
                    {payout.amount} <span className="text-ink-faint">{payout.currency}</span>
                  </Td>
                  <Td align="right" className="whitespace-nowrap text-[12px] text-ink-muted">
                    {formatDateTime(payout.created_at)}
                  </Td>
                  <Td align="right">
                    <div className="flex justify-end gap-1.5">
                      {payout.status === "pending" && (
                        <Button size="sm" onClick={() => setAction({ kind: "processing", payout })}>
                          Instructed
                        </Button>
                      )}
                      {payout.status === "processing" && (
                        <>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => setAction({ kind: "confirm", payout })}
                          >
                            Confirm
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setAction({ kind: "fail", payout })}>
                            Failed
                          </Button>
                        </>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Sheet>

      <Sheet className="settle settle-2 mt-6 overflow-hidden">
        <SheetHeader title="Running a payout" />
        <ol className="space-y-3.5 px-5 py-4">
          {[
            [
              "Create the payout",
              "From the firm's page. This gathers their cleared payments and moves the funds out of their available balance, so the same money cannot be settled twice.",
            ],
            [
              "Instruct the transfer at the bank",
              "Then mark it Instructed here, so anyone else looking at the queue knows it is already in hand.",
            ],
            [
              "Confirm with the bank reference",
              "Once the transfer settles. The reference is required — without it a disputed payout cannot be reconciled.",
            ],
            [
              "If it fails",
              "Mark it failed with a reason. The funds return to the firm's balance and the payout is closed.",
            ],
          ].map(([title, body], i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-rule-strong font-mono text-[10px]">
                {i + 1}
              </span>
              <div>
                <p className="text-[13px] font-medium text-ink">{title}</p>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-muted">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </Sheet>

      <Modal
        open={action !== null}
        onClose={() => setAction(null)}
        title={
          action?.kind === "processing"
            ? "Mark as instructed?"
            : action?.kind === "confirm"
              ? "Confirm this payout"
              : "Mark this payout as failed"
        }
        description={
          action?.kind === "processing"
            ? "Record that you have instructed the transfer at the bank."
            : action?.kind === "confirm"
              ? "Record the bank reference for the settled transfer."
              : "The funds return to the firm's available balance."
        }
        footer={
          <>
            <Button onClick={() => setAction(null)}>Cancel</Button>
            <Button
              variant={action?.kind === "fail" ? "danger" : "primary"}
              loading={act.isPending}
              disabled={
                (action?.kind === "confirm" && reference.trim() === "") ||
                (action?.kind === "fail" && reason.trim() === "")
              }
              onClick={() => act.mutate()}
            >
              {action?.kind === "processing"
                ? "Mark instructed"
                : action?.kind === "confirm"
                  ? "Confirm payout"
                  : "Mark failed"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <ErrorNotice error={act.error} />
          {action && (
            <p className="text-[13px] text-ink-muted">
              {action.payout.amount} {action.payout.currency} to {action.payout.destination.account_name} at{" "}
              {action.payout.destination.bank_name}.
            </p>
          )}
          {action?.kind === "confirm" && (
            <Field
              label="Bank reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="ZB-TRF-99871"
              required
              hint="Required. This is what a disputed payout is reconciled against."
            />
          )}
          {action?.kind === "fail" && (
            <>
              <TextAreaField
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Beneficiary account details rejected by the bank."
                required
              />
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
